import type { AuthnMethod, FactorOption } from '@imprivata-cloud/authn';
import { debug } from '@imprivata-cloud/common';
import { useMemo } from 'react';

import { QueryParam } from '@/constants';

export const useQueryParams = () => {
  const search = window.location.search; // primitive string

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const applicationId = queryParams.get(QueryParam.ApplicationId) ?? undefined;
  const tenantId = queryParams.get(QueryParam.TenantId) ?? '';
  const rpRequestId = queryParams.get(QueryParam.RpRequestId) ?? '';
  const hideFactorSwitch = !queryParams.get(QueryParam.ShowAddMethod);
  const loginHint = queryParams.get(QueryParam.LoginHint);

  const contextResource = useMemo(() => {
    if (applicationId) {
      return { resourceType: '', contextType: '' };
    }
    return {
      resourceType: queryParams.get(QueryParam.ResourceType) ?? '',
      contextType: queryParams.get(QueryParam.ContextType) ?? '',
    };
  }, [applicationId, queryParams]);

  const oidcRequestData = useMemo(
    () => ({
      codeChallenge: queryParams.get(QueryParam.CodeChallenge) ?? '',
      codeChallengeMethod:
        queryParams.get(QueryParam.CodeChallengeMethod) ?? '',
    }),
    [queryParams],
  );

  const initialFactors = useMemo(() => {
    const factors = queryParams.get(QueryParam.AcrValues);
    if (Array.isArray(factors)) {
      return factors.map(factor => ({
        factorType: factor as AuthnMethod,
      })) as FactorOption[];
    }
    return factors ? ([{ factorType: factors }] as FactorOption[]) : undefined;
  }, [queryParams]);

  const fitVideoFrameString = queryParams.get(QueryParam.FitVideoFrame);
  const fitVideoFrame = fitVideoFrameString
    ? fitVideoFrameString === 'true'
    : undefined;

  const acrValues = queryParams.get(QueryParam.AcrValues);
  const isOneToMany = !loginHint && acrValues === 'face';

  debug('[QUERY-PARAMS]', {
    initialFactors,
    oidcRequestData,
    applicationId,
    tenantId,
    rpRequestId,
    hideFactorSwitch,
    contextResource,
    fitVideoFrame,
    loginHint,
    isOneToMany,
  });
  return {
    initialFactors,
    oidcRequestData,
    applicationId,
    tenantId,
    rpRequestId,
    hideFactorSwitch,
    contextResource,
    fitVideoFrame,
    loginHint,
    isOneToMany,
  };
};
